import React, { useState } from 'react';
import { USDateFormat } from '../../../../utils/common';
import {
  BsEyeFill,
  BsHouseCheckFill,
  BsPauseCircleFill,
  BsPlayCircleFill,
} from 'react-icons/bs';
import { FaEdit } from 'react-icons/fa';
import { BiSolidTrash } from 'react-icons/bi';
import { Skeleton } from '@mui/material';
import PropertySample from '../../../../assets/images/PropertySample.jpg';
import {
  dealTypeEnum,
  proprertyViewData,
} from '../../../../constants/Property';
import './ListingsRowItem.scss';

interface IProps {
  item: any;
  gotoPropertyDetail: (id: string, dealType: string) => void;
  handleEdit: (id: string, dealType: string) => void;
  handleDelete: (item: any) => void;
  changeStatusProperty: (
    id: string,
    isActive: boolean,
    status: string,
    dealType: string
  ) => void;
}

function ListingsRowItem(props: IProps) {
  const {
    item,
    gotoPropertyDetail,
    handleEdit,
    handleDelete,
    changeStatusProperty,
  } = props;
  const [imageLoading, setImageLoading] = useState(true);
  // Extract dynamic values to avoid messy inline conditionals
  const isSell = item?.dealType === dealTypeEnum?.Sell;

  const previewImage = isSell
    ? item?.images?.length > 0
      ? item?.images[0]
      : PropertySample
    : item?.rooms?.[0]?.imagesLink?.length > 0
      ? item?.rooms?.[0]?.imagesLink[0]
      : PropertySample;

  const availableDate = isSell
    ? item?.available_date
    : item?.rooms?.[0]?.availableFrom;

  const price = isSell ? item?.price : item?.rooms?.[0]?.price;

  const statusLabel = item?.is_active
    ? 'Active'
    : item?.status === 'Rented'
      ? 'Rented'
      : isSell
        ? 'Sold'
        : 'Inactive';

  const statusBadgeClass = item?.is_active
    ? 'for-active'
    : item?.status === 'Rented'
      ? 'for-rent'
      : isSell
        ? 'for-sold'
        : 'for-inactive';

  return (
    <tr>
      {/* 1. Property Preview (Mobile Hero Section) */}
      <td>
        <div className="d-flex align-items-center gap-3">
          <div className="property-listing-preview">
            {imageLoading && (
              <Skeleton
                animation="wave"
                variant="rounded"
                width={52}
                height={52}
              />
            )}
            <img
              src={previewImage}
              alt={item?.title || 'Property Preview'}
              className="img-fluid"
              onLoad={() => setImageLoading(false)}
              style={{ display: imageLoading ? 'none' : 'block' }}
            />
          </div>
          <div
            className="property-details select-none"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => gotoPropertyDetail(item?._id, item?.dealType)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                gotoPropertyDetail(item?._id, item?.dealType);
              }
            }}
          >
            <h5
              className="mb-1 text-primary text-decoration-none-hover"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item?.title || '-'}
            </h5>
            <p
              className="mb-0 text-muted"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item?.description || item?.address?.street || '-'}
            </p>
          </div>
        </div>
      </td>

      {/* 2. Structured Data Columns with Mobile Labels */}
      <td data-label="Listing Type">
        <span className="badge-type">{isSell ? 'Sell' : 'Rent'}</span>
      </td>

      <td data-label="Available From">{USDateFormat(availableDate) || '-'}</td>

      <td data-label="Property Type" className="text-capitalize">
        {proprertyViewData?.[item?.p_type] || '-'}
      </td>

      <td data-label="Featured">{item?.isFeaturedProperty ? 'Yes' : 'No'}</td>

      <td data-label="Price">
        <span className="price-tag">{price ? `₹${price}` : '-'}</span>
      </td>

      <td data-label="Amenities">
        {item?.amenities?.length < 3
          ? item?.amenities?.map((i: any) => i?.name).join(', ')
          : `${item?.amenities?.length}+ more`}
      </td>

      <td data-label="Status">
        <span className={`status ${statusBadgeClass}`}>• {statusLabel}</span>
      </td>

      {/* 3. Responsive Action Buttons */}
      <td className="text-end-responsive">
        <div className="d-flex align-items-center justify-content-center gap-2 action-group">
          {/* View Details */}
          <button
            type="button"
            className="btn-action btn-action-view"
            title="View Details"
            onClick={() => gotoPropertyDetail(item?._id, item?.dealType)}
          >
            <BsEyeFill />
          </button>

          {/* Edit Property */}
          <button
            type="button"
            className="btn-action btn-action-edit"
            title="Edit Property"
            onClick={() => handleEdit(item?._id, item?.dealType)}
          >
            <FaEdit />
          </button>

          {/* Dynamic Active/Inactive Toggle */}
          {item?.is_active ? (
            <button
              type="button"
              className="btn-action"
              style={{ color: '#f59e0b' }}
              title="Mark Inactive"
              onClick={() =>
                changeStatusProperty(
                  item?._id,
                  false,
                  'Inactive',
                  item?.dealType
                )
              }
            >
              <BsPauseCircleFill />
            </button>
          ) : (
            <button
              type="button"
              className="btn-action"
              style={{ color: '#10b981' }}
              title="Mark Active"
              onClick={() =>
                changeStatusProperty(
                  item?._id,
                  true,
                  'Available',
                  item?.dealType
                )
              }
            >
              <BsPlayCircleFill />
            </button>
          )}

          {/* Mark Rented / Sold */}
          <button
            type="button"
            className="btn-action"
            style={{ color: '#8b5cf6' }}
            title={isSell ? 'Mark Sold' : 'Mark Rented'}
            onClick={() =>
              changeStatusProperty(
                item?._id,
                false,
                isSell ? 'Sold' : 'Rented',
                item?.dealType
              )
            }
          >
            <BsHouseCheckFill />
          </button>

          {/* Delete Property */}
          <button
            type="button"
            className="btn-action btn-action-delete"
            title="Delete Property"
            onClick={() => handleDelete(item)}
          >
            <BiSolidTrash />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default ListingsRowItem;
